// React
import classnames from 'classnames';
import Dropzone from 'react-dropzone';
import { useEffect, useState } from 'react';
import {
  Nav,
  Col,
  Row,
  Card,
  Form,
  Label,
  Input,
  TabPane,
  NavItem,
  NavLink,
  CardBody,
  CardFooter,
  CardHeader,
  TabContent,
} from 'reactstrap';
import Select from 'react-select';
import 'cleave.js/dist/addons/cleave-phone.in';

// Yup Validation
import * as Yup from 'yup';
import { useFormik } from 'formik';

// Redux
import { useHistory, useParams } from 'react-router-dom';

// Components & Injections
import Section from './Section';
import { WhatsappTab } from '../../components/whatsappTab';
import { useLoading } from '../../containers/hooksInjection';
import { PlansInjection } from '../../containers/plansInjection';
import { CategoryPlansInjection } from '../../containers/categoryPlansInjection';
import {
  Currency,
  PageWrapper,
  notifyError,
  LoadingScreen,
  PaymentLinksTab,
  FileAttachmentCard,
} from '../../containers/componentsInjection';

// Helpers & Utils
import { APIClient } from '../../../../helpers/api_helper';
import { isABase64 } from '../../../../utils/files/isABase64';
import { convertEventSelectToTarget } from '../../../../utils/createEvent';
import { formatPaymentLinksToSendForAPI } from '../../utils/formatPaymentLinksToSendForAPI';

const PlansFormEdit = () => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [paymentLinks, setPaymentLinks] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [whatsappMedias, setWhatsappMedias] = useState([]);

  const { createPlans, updatePlans } = PlansInjection();
  const { categoryPlans, getCategoryPlans } = CategoryPlansInjection();

  const { id } = useParams();
  const api = new APIClient();
  const history = useHistory();
  const { loadingWithTime } = useLoading();

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const formatCategoryPlansForOptionsSelect = () => {
    let data = categoryPlans;
    let newData = (data || []).map((categoryPlan) => {
      return {
        value: categoryPlan.id,
        label: categoryPlan.name,
      };
    });
    return newData;
  };

  const getPaymentLinks = (plan) => {
    const { links } = plan;

    const formattedLinks = {};

    (links || []).forEach((link) => {
      formattedLinks[link.office_id] = link.link;
    });

    setPaymentLinks(formattedLinks);
  };

  const getPlanById = async (id) => {
    setLoading(true);

    try {
      const response = await api.get(`/register/plan/${id}`);

      if (response) {
        setPlan(response);
        getPaymentLinks(response);
        if (response?.contract_model) {
          setSelectedFile({
            action: 'keep',
            file: response?.proposal_template_filename,
            data: response?.contract_model,
          });
        }

        const whatsappMedias = [
          {
            id: response.message_midia1_id || '',
            file: null,
            preview: response.message_midia1_url || null,
            fileName: response.message_midia1_filename || '',
            caption: response.message_midia1_caption || '',
          },
          {
            id: response.message_midia2_id || '',
            file: null,
            preview: response.message_midia2_url || null,
            fileName: response.message_midia2_filename || '',
            caption: response.message_midia2_caption || '',
          },
        ];

        setWhatsappMedias(whatsappMedias);
      }
    } catch (error) {
      console.error('Erro ao buscar o plano:', error);
    } finally {
      setLoading(false);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      nameInput: plan ? plan.name : '',
      periodicidadeInput: plan
        ? {
            value: plan.periodicity_payment,
            label: plan.periodicity_payment === 'M' ? 'Mensal' : 'Anual',
          }
        : null,
      dependentsCoveredInput: plan ? plan.covered_dependents_qty : '',
      additionalPriceDependents: plan ? plan.add_price_dependent : '0.00',
      categoryPlanInput: plan
        ? { value: plan.plan_category_id, label: plan.category_name }
        : '',
      priceInput: plan ? plan.price : '0.00',
      numberMaxDependents: plan ? plan.max_dependents : '',

      blockFinalValueProposal:
        plan?.block_value_proposal === true ? true : false,

      isDisponibleInSite: plan?.available_site === true ? true : false,
      isShowMember: plan?.show_membership_fee === true ? true : false,

      // isActive: plan?.active === true ? true : false,
      generateNumber: plan?.generate_card_number === true ? true : false,
      isCalculateComission: plan?.calculate_commission === true ? true : false,
      isFuneralAssistance: plan?.funeral_assistance === true ? true : false,
      appAccess: plan?.app_access === true ? true : false,

      isNutritionalAssistance:
        plan?.nutritional_assistance === true ? true : false,
      teleorientation: plan?.teleorientation === true ? true : false,
      telemedicine: plan?.telemedicine === true ? true : false,

      commissionCalcBase: plan ? plan.commission_calc_base : '0.00',
      recurringCommission: plan ? plan.recurring_commission : '0.00',

      dental_plan: plan?.dental_plan === true ? true : false,
      deathInsurance: plan?.death_insurance === true ? true : false,
      disabilityInsurance: plan?.disability_insurance === true ? true : false,
      fractureInsurance: plan?.fracture_insurance === true ? true : false,
      monthlyDraw: plan?.monthly_draw === true ? true : false,
    },
    validationSchema: Yup.object({
      dependentsCoveredInput: Yup.string().required(
        'Qtd. Dependentes Cobertos é Obrigatório'
      ),
      additionalPriceDependents: Yup.string(),
      nameInput: Yup.string().required('Nome é obrigatório'),
      periodicidadeInput: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable(),
        })
        .nullable()
        .required('Periodicidade é obrigatório'),
      categoryPlanInput: Yup.object()
        .nullable()
        .required('Categoria é obrigatório'),
      priceInput: Yup.string(),
      numberMaxDependents: Yup.string().required('Qtd. Máximo de Dependentes'),
      // isActiveSelect: Yup.string().nullable(),
      isShowMember: Yup.boolean(),
      // isActive: Yup.boolean(),
      blockFinalValueProposal: Yup.boolean(),
      isDisponibleInSite: Yup.boolean(),
      generateNumber: Yup.boolean(),
      isCalculateComission: Yup.boolean(),
      isFuneralAssistance: Yup.boolean(),
      appAccess: Yup.boolean(),
      isNutritionalAssistance: Yup.boolean(),
      teleorientation: Yup.boolean(),
      telemedicine: Yup.boolean(),

      commissionCalcBase: Yup.string(),
      recurringCommission: Yup.string(),

      dental_plan: Yup.boolean(),
      deathInsurance: Yup.boolean(),
      disabilityInsurance: Yup.boolean(),
      fractureInsurance: Yup.boolean(),
      monthlyDraw: Yup.boolean(),
    }),

    onSubmit: (values) => {
      const valuesToSubmit = {
        name: values.nameInput || null,
        plan_category_id: values.categoryPlanInput.value || null,
        periodicity_payment: values.periodicidadeInput.value || null,
        price: values.priceInput.toString(),
        covered_dependents_qty:
          values.dependentsCoveredInput.toString() || null,
        add_price_dependent: values.additionalPriceDependents.toString(),
        max_dependents: values.numberMaxDependents.toString() || null,
        show_membership_fee: values.isShowMember || false,
        generate_card_number: values.generateNumber || false,
        available_site: values.isDisponibleInSite || false,
        block_value_proposal: values.blockFinalValueProposal || false,
        // active: values.isActive || true,
        calculate_commission: values.isCalculateComission || false,
        funeral_assistance: values.isFuneralAssistance || false,
        app_access: values.appAccess || false,
        nutritional_assistance: values.isNutritionalAssistance || false,
        teleorientation: values.teleorientation || false,
        telemedicine: values.telemedicine || false,

        dental_plan: values.dental_plan || false,
        death_insurance: values.deathInsurance || false,
        disability_insurance: values.disabilityInsurance || false,
        fracture_insurance: values.fractureInsurance || false,
        monthly_draw: values.monthlyDraw || false,

        commission_calc_base: values.commissionCalcBase.toString(),
        recurring_commission: values.recurringCommission.toString(),

        links: formatPaymentLinksToSendForAPI(paymentLinks),

        contract_model: selectedFile,

        // error if you do not send this key
        proposal_template_s3_key: null,
      };

      loadingWithTime(1000);
      if (plan) {
        updatePlans(plan.id, { ...valuesToSubmit, id: plan.id });
        history.push('/planos');
      } else {
        createPlans(valuesToSubmit);
        history.push('/planos');
      }
    },
  });

  useEffect(() => {
    getCategoryPlans();
  }, []);

  useEffect(() => {
    if (id) {
      getPlanById(id);
    }
  }, [id]);

  return (
    <LoadingScreen loading={loading} titleMetaTag={'Planos'}>
      <PageWrapper title={'Planos'}>
        <Row>
          <Col>
            <Form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                {plan && (
                  <CardHeader>
                    <Nav
                      className="nav-tabs-custom rounded card-header-tabs border-bottom-0"
                      role="tablist"
                    >
                      <NavItem>
                        <NavLink
                          className={classnames({
                            active: activeTab === '1',
                          })}
                          onClick={() => {
                            tabChange('1');
                          }}
                          style={{ cursor: 'pointer' }}
                        >
                          <i className="fas fa-home"></i>
                          Dados do Plano
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          to="#"
                          className={classnames({
                            active: activeTab === '2',
                          })}
                          onClick={() => {
                            tabChange('2');
                          }}
                          type="button"
                        >
                          WhatsApp
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink
                          to="#"
                          className={classnames({
                            active: activeTab === '3',
                          })}
                          onClick={() => {
                            tabChange('3');
                          }}
                          type="button"
                        >
                          <i className="far fa-user"></i>
                          Links de Pagamento
                        </NavLink>
                      </NavItem>
                    </Nav>
                  </CardHeader>
                )}

                <TabContent activeTab={activeTab}>
                  <TabPane tabId="1">
                    <CardBody>
                      <div className="live-preview">
                        <Section
                          title={plan ? 'Editar Plano' : 'Novo Plano'}
                          label={
                            plan
                              ? 'Preencha os campos para editar um plano.'
                              : 'Preencha os campos para adicionar um novo plano.'
                          }
                        />
                        <Row>
                          <Col md={6}>
                            <div className="mb-3">
                              <Label htmlFor="name" className="form-label">
                                Nome
                              </Label>
                              <Input
                                type="text"
                                className="form-control"
                                id="nameInput"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={validation.values.nameInput || ''}
                              />
                              {validation.touched.nameInput &&
                              validation.errors.nameInput ? (
                                <p className="labelErrorValidation">
                                  {validation.errors.nameInput}
                                </p>
                              ) : null}
                            </div>
                          </Col>
                          <Col md={3}>
                            <div className="mb-3">
                              <Label
                                htmlFor="categoryPlanInput"
                                className="form-label"
                              >
                                Categoria
                              </Label>
                              <Select
                                isClearable={true}
                                value={validation.values.categoryPlanInput}
                                onChange={(e) => {
                                  const event = {
                                    target: {
                                      name: 'categoryPlanInput',
                                      value: e,
                                    },
                                  };
                                  validation.handleChange(event);
                                }}
                                onBlur={validation.handleBlur}
                                options={
                                  formatCategoryPlansForOptionsSelect() || []
                                }
                                inputId="categoryPlanInput"
                              />
                              {validation.touched.categoryPlanInput &&
                              validation.errors.categoryPlanInput ? (
                                <p className="labelErrorValidation">
                                  {validation.errors.categoryPlanInput}
                                </p>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <Label
                                htmlFor="periodicidadeInput"
                                className="form-label"
                              >
                                Periodicidade
                              </Label>

                              <Select
                                isClearable={true}
                                value={validation.values.periodicidadeInput}
                                options={[
                                  {
                                    value: 'm',
                                    label: 'Mensal',
                                  },
                                  {
                                    value: 'y',
                                    label: 'Anual',
                                  },
                                ]}
                                onChange={(e) => {
                                  const event = {
                                    target: {
                                      name: 'periodicidadeInput',
                                      value: e,
                                    },
                                  };
                                  validation.handleChange(event);
                                }}
                                onBlur={validation.handleBlur}
                                inputId="periodicidadeInput"
                              />
                              {validation.touched.periodicidadeInput &&
                              validation.errors.periodicidadeInput ? (
                                <p className="labelErrorValidation">
                                  {validation.errors.periodicidadeInput}
                                </p>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <label
                                htmlFor="priceInput"
                                className="form-label"
                              >
                                Preço Inicial
                              </label>

                              <Currency
                                id="priceInput"
                                name="priceInput"
                                onChange={(_, valueWithoutMask) => {
                                  validation.handleChange(
                                    convertEventSelectToTarget(
                                      'priceInput',
                                      valueWithoutMask
                                    )
                                  );
                                }}
                                onBlur={validation.handleBlur}
                                value={validation.values.priceInput || ''}
                              />
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <Label htmlFor="name" className="form-label">
                                Qtd. Dependentes Cobertos
                              </Label>
                              <Input
                                type="number"
                                className="form-control"
                                id="dependentsCoveredInput"
                                name="dependentsCoveredInput"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={
                                  validation.values.dependentsCoveredInput || ''
                                }
                              />
                              {validation.touched.dependentsCoveredInput &&
                              validation.errors.dependentsCoveredInput ? (
                                <p className="labelErrorValidation">
                                  {validation.errors.dependentsCoveredInput}
                                </p>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <Label htmlFor="name" className="form-label">
                                Preço Adicional por Dependentes
                              </Label>

                              <Currency
                                className="form-control"
                                id="additionalPriceDependents"
                                name="additionalPriceDependents"
                                onChange={(_, additionalPriceDependents) => {
                                  validation.handleChange(
                                    convertEventSelectToTarget(
                                      'additionalPriceDependents',
                                      additionalPriceDependents
                                    )
                                  );
                                }}
                                onBlur={validation.handleBlur}
                                value={
                                  validation.values.additionalPriceDependents ||
                                  ''
                                }
                              />
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <Label htmlFor="name" className="form-label">
                                Qtd. Máximo de Dependentes
                              </Label>
                              <Input
                                type="number"
                                className="form-control"
                                id="numberMaxDependents"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                value={
                                  validation.values.numberMaxDependents || ''
                                }
                              />
                              {validation.touched.numberMaxDependents &&
                              validation.errors.numberMaxDependents ? (
                                <p className="labelErrorValidation">
                                  {validation.errors.numberMaxDependents}
                                </p>
                              ) : null}
                            </div>
                          </Col>

                          <Col md={2}>
                            <div
                              className="form-check form-switch form-switch-md"
                              style={{
                                marginTop: 35,
                              }}
                            >
                              <Input
                                role="switch"
                                type="checkbox"
                                id="isCalculateComission"
                                className="form-check-input"
                                onBlur={validation.handleBlur}
                                onChange={validation.handleChange}
                                checked={validation.values.isCalculateComission}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="isCalculateComission"
                              >
                                Gera comissão
                              </Label>
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <label
                                htmlFor="commissionCalcBase"
                                className="form-label"
                              >
                                Base de cálculo de comissão
                              </label>

                              <Currency
                                id="commissionCalcBase"
                                name="commissionCalcBase"
                                onChange={(_, valueWithoutMask) => {
                                  validation.handleChange(
                                    convertEventSelectToTarget(
                                      'commissionCalcBase',
                                      valueWithoutMask
                                    )
                                  );
                                }}
                                onBlur={validation.handleBlur}
                                value={
                                  validation.values.commissionCalcBase || ''
                                }
                                disabled={
                                  validation.values.isCalculateComission ===
                                  false
                                }
                              />
                            </div>
                          </Col>

                          <Col md={3}>
                            <div className="mb-3">
                              <Label
                                htmlFor="recurringCommission"
                                className="form-label"
                              >
                                Comissão
                              </Label>
                              <div className="input-group">
                                <Currency
                                  config={{
                                    locale: 'pt-BR',
                                    formats: {
                                      number: {
                                        BRL: {
                                          currency: 'BRL',
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        },
                                      },
                                    },
                                  }}
                                  className="form-control form-control-suffix"
                                  id="recurringCommission"
                                  maxLength="6"
                                  onChange={(_, valueWithoutMask) => {
                                    validation.handleChange(
                                      convertEventSelectToTarget(
                                        'recurringCommission',
                                        valueWithoutMask
                                      )
                                    );
                                  }}
                                  onBlur={validation.handleBlur}
                                  value={
                                    validation.values.recurringCommission || ''
                                  }
                                  disabled={
                                    validation.values.isCalculateComission ===
                                    false
                                  }
                                />
                                <span
                                  className="input-group-text"
                                  id="basic-addon2"
                                >
                                  %
                                </span>
                              </div>
                            </div>
                          </Col>

                          <Col md={4}>
                            <div className="mb-3">
                              <Label className="form-label">
                                Modelo do Contrato
                              </Label>
                              <Dropzone
                                uploadMultiple={false}
                                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onDrop={async (acceptedFiles) => {
                                  if (acceptedFiles.length > 0) {
                                    // verify if the file has .docx extension
                                    const file = acceptedFiles[0];
                                    if (file.name.endsWith('.docx')) {
                                      const fileToBase64 = (file) =>
                                        new Promise((resolve, reject) => {
                                          const reader = new FileReader();
                                          reader.onload = () => {
                                            const base64Data =
                                              reader.result.split(',')[1]; // remove the prefix "data:image/png;base64,"
                                            resolve(base64Data);
                                          };
                                          reader.onerror = () => {
                                            reject(
                                              new Error(
                                                'Failed to read file as base64'
                                              )
                                            );
                                          };
                                          reader.readAsDataURL(file);
                                        });

                                      try {
                                        const fileAsBase64 = await fileToBase64(
                                          file
                                        );
                                        setSelectedFile(
                                          Object.assign({
                                            file: file.path,
                                            data: fileAsBase64,
                                            action: 'upload',
                                          })
                                        );
                                      } catch (error) {
                                        console.error(
                                          'Erro ao converter arquivo para base64:',
                                          error
                                        );
                                      }
                                    }
                                  }
                                }}
                                onDropRejected={(fileRejections) => {
                                  notifyError(
                                    'Por favor, adicione apenas arquivos .docx'
                                  );
                                }}
                              >
                                {({ getRootProps }) => (
                                  <div
                                    className="dz-message needsclick d-flex align-items-center"
                                    {...getRootProps()}
                                  >
                                    <small className="fw-semibold me-1">
                                      <i>Selecione o arquivo desejado</i>
                                    </small>
                                    <i className="ri-attachment-2 fs-3 text-muted fw-bold"></i>
                                  </div>
                                )}
                              </Dropzone>

                              <Col>
                                {selectedFile && (
                                  <FileAttachmentCard
                                    fileName={selectedFile?.file}
                                    handleDelete={() => setSelectedFile(null)}
                                    fileLink={
                                      !isABase64(selectedFile?.data)
                                        ? selectedFile?.data
                                        : null
                                    }
                                  />
                                )}
                              </Col>
                            </div>
                          </Col>

                          <Row md={12} className="mt-3">
                            <Col md={6}>
                              <h5 className="mb-3 fw-bold">
                                Serviços Cobertos
                              </h5>
                              <div className="border-end border-start">
                                <div className="card-body d-flex">
                                  <Col>
                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="appAccess"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        checked={validation.values.appAccess}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="appAccess"
                                      >
                                        Acesso ao Aplicativo
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="isFuneralAssistance"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        checked={
                                          validation.values.isFuneralAssistance
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="isFuneralAssistance"
                                      >
                                        Assistência funenária
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        role="switch"
                                        type="checkbox"
                                        id="isNutritionalAssistance"
                                        className="form-check-input"
                                        onBlur={validation.handleBlur}
                                        onChange={validation.handleChange}
                                        checked={
                                          validation.values
                                            .isNutritionalAssistance
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="isNutritionalAssistance"
                                      >
                                        Assistência nutricional
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        role="switch"
                                        type="checkbox"
                                        id="teleorientation"
                                        className="form-check-input"
                                        onBlur={validation.handleBlur}
                                        onChange={validation.handleChange}
                                        checked={
                                          validation.values.teleorientation
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="teleorientation"
                                      >
                                        Orientação médica por telefone
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        role="switch"
                                        type="checkbox"
                                        id="telemedicine"
                                        className="form-check-input"
                                        onBlur={validation.handleBlur}
                                        onChange={validation.handleChange}
                                        checked={validation.values.telemedicine}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="telemedicine"
                                      >
                                        Telemedicina
                                      </Label>
                                    </div>
                                  </Col>

                                  <Col>
                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="dental_plan"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        checked={validation.values.dental_plan}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="dental_plan"
                                      >
                                        Plano Odontológico
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="deathInsurance"
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        checked={
                                          validation.values.deathInsurance
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="deathInsurance"
                                      >
                                        Seguro Morte Acidental
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        role="switch"
                                        type="checkbox"
                                        id="disabilityInsurance"
                                        className="form-check-input"
                                        onBlur={validation.handleBlur}
                                        onChange={validation.handleChange}
                                        checked={
                                          validation.values.disabilityInsurance
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="disabilityInsurance"
                                      >
                                        Seguro Invalidez
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        role="switch"
                                        type="checkbox"
                                        id="fractureInsurance"
                                        className="form-check-input"
                                        onBlur={validation.handleBlur}
                                        onChange={validation.handleChange}
                                        checked={
                                          validation.values.fractureInsurance
                                        }
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="fractureInsurance"
                                      >
                                        Seguro Fratura Óssea
                                      </Label>
                                    </div>

                                    <div className="form-check form-switch mb-3 form-switch-md">
                                      <Input
                                        role="switch"
                                        type="checkbox"
                                        id="monthlyDraw"
                                        className="form-check-input"
                                        onBlur={validation.handleBlur}
                                        onChange={validation.handleChange}
                                        checked={validation.values.monthlyDraw}
                                      />
                                      <Label
                                        className="form-check-label"
                                        htmlFor="monthlyDraw"
                                      >
                                        Sorteio Mensal
                                      </Label>
                                    </div>
                                  </Col>
                                </div>
                              </div>
                            </Col>

                            <Col md={6}>
                              <div className="card-body">
                                <h5 className="mb-3 fw-bold">Configurações</h5>
                                <div className="form-check form-switch mb-3 form-switch-md">
                                  <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="blockFinalValueProposal"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    checked={
                                      validation.values.blockFinalValueProposal
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor="blockFinalValueProposal"
                                  >
                                    Bloquear valor na proposta
                                  </Label>
                                </div>

                                <div className="form-check form-switch mb-3 form-switch-md">
                                  <Input
                                    className="form-check-input "
                                    type="checkbox"
                                    role="switch"
                                    id="isShowMember"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    checked={validation.values.isShowMember}
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor="isShowMember"
                                  >
                                    Taxa de adesão
                                  </Label>
                                </div>

                                <div className="form-check form-switch mb-3 form-switch-md">
                                  <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="generateNumber"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    checked={validation.values.generateNumber}
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor="generateNumber"
                                  >
                                    Gerar número de cartão
                                  </Label>
                                </div>

                                <div className="form-check form-switch mb-3 form-switch-md">
                                  <Input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="isDisponibleInSite"
                                    onChange={validation.handleChange}
                                    onBlur={validation.handleBlur}
                                    checked={
                                      validation.values.isDisponibleInSite
                                    }
                                  />
                                  <Label
                                    className="form-check-label"
                                    htmlFor="isDisponibleInSite"
                                  >
                                    Disponível no site
                                  </Label>
                                </div>
                              </div>
                            </Col>
                          </Row>

                          {/* <Row md={12}>
                          <Col md={3}>
                            <div className="form-check form-switch mb-3 form-switch-md">
                              <Input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="isActive"
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                checked={validation.values.isActive}
                              />
                              <Label
                                className="form-check-label"
                                htmlFor="isActive"
                              >
                                Ativo
                              </Label>
                            </div>
                          </Col>
                        </Row> */}
                        </Row>
                      </div>
                    </CardBody>

                    <CardFooter>
                      <Col md={12}>
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="card-text">
                            <small className="text-muted">
                              {plan?.last_update}
                            </small>
                          </span>
                          <div className="text-end">
                            <button
                              type="button"
                              className="btn btn-link"
                              style={{
                                marginRight: '10px',
                              }}
                              onClick={() => {
                                history.push('/planos');
                              }}
                            >
                              Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary">
                              Salvar
                            </button>
                          </div>
                        </div>
                      </Col>
                    </CardFooter>
                  </TabPane>

                  <TabPane tabId="2">
                    <WhatsappTab
                      planId={id}
                      plan={plan}
                      getPlanById={getPlanById}
                      whatsappMedias={whatsappMedias}
                      setWhatsappMedias={setWhatsappMedias}
                    />
                  </TabPane>

                  <TabPane tabId="3">
                    <CardBody className="p-4">
                      <PaymentLinksTab
                        links={plan?.links}
                        paymentLinks={paymentLinks}
                        setPaymentLinks={setPaymentLinks}
                      />
                    </CardBody>
                  </TabPane>
                </TabContent>
              </Card>
            </Form>
          </Col>
        </Row>
      </PageWrapper>
    </LoadingScreen>
  );
};

export default PlansFormEdit;
